/*
PURPOSE:
This client-side middleware synchronizes our authentication state with our Pinia store.
It ensures that the user's permissions data is always kept in sync with our Pinia store,
which is used throughout the app for permission checks.

HOW IT WORKS:
1. It runs on every page load/navigation
2. It checks the authentication status
3. If the user is authenticated, it copies their email and permissions to our Pinia store
4. If the user has logged out, it clears their data from our store

WHY IT'S NEEDED:
Without this middleware, our Pinia store wouldn't automatically know about authentication
changes or have access to the user's permissions data. This middleware creates a bridge
between Nuxt Auth and our Pinia store. This will also prevent users from trying to manipulate
their local storage to gain access to restricted areas of the app. As soon as they tried
navigating anywhere, their client-side permissions would be replaced with the real
server-side permissions.
*/

import { useUserStore } from '~/stores/user'

export default defineNuxtRouteMiddleware(() => {
  // Read auth state without useAuth() — it registers onMounted and crashes when
  // called in middleware during a click-triggered client navigation. See useAuthSnapshot.
  const { data, status } = useAuthSnapshot()
  const userStore = useUserStore()

  // When authenticated, update the user store with session data
  if (status.value === 'authenticated' && data.value?.user) {
    userStore.email = data.value.user.email
    userStore.permissions = data.value.user.permissions
    userStore.firstName = data.value.user.name.first
    userStore.lastName = data.value.user.name.last
    userStore.displayName = data.value.user.name.display
    userStore.vendorCode = data.value.user.vendorCode
  }

  // If logged out in auth but still has data in store, clear it
  if (status.value === 'unauthenticated' && userStore.email) {
    userStore.clearSession()
  }
})
